import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatDate, laneLabel, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ListChecks, CheckSquare, HandCoins, DollarSign, ArrowRight, Building2 } from "lucide-react";

const Stat = ({ icon: Icon, value, label, color }) => (
  <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="partner-stat">
    <Icon className="mb-2" size={22} style={{ color: color || "#1C4358" }} />
    <div className="text-3xl font-bold font-display text-[#252221] tabular-nums">{value}</div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </Card>
);

export default function PartnerPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await api.get("/portal/partner/overview"); setData(res.data); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="space-y-4"><Skeleton className="h-20" /><div className="grid grid-cols-4 gap-4"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div></div>;

  const s = data?.stats || {};
  const refs = data?.recent_referrals || [];
  const tasks = data?.recent_tasks || [];
  const profile = data?.profile || {};

  return (
    <div className="space-y-6" data-testid="partner-portal">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#252221]">Welcome, {profile.name || user?.full_name}</h2>
        <p className="text-sm text-muted-foreground">Your referrals, tasks, and payouts with MovingMyMama.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={ListChecks} value={s.referrals ?? 0} label="Assigned Referrals" />
        <Stat icon={CheckSquare} value={s.open_tasks ?? 0} label="Open Tasks" />
        <Stat icon={HandCoins} value={formatCurrency(s.total_owed || 0)} label="Owed to You" color="#B7791F" />
        <Stat icon={DollarSign} value={formatCurrency(s.total_paid || 0)} label="Paid to Date" color="#2F7D5A" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-[#252221]">Recent referrals</h3>
            <Button variant="outline" size="sm" onClick={() => navigate("/portal/referrals")} data-testid="partner-view-referrals">View all <ArrowRight size={14} className="ml-1.5" /></Button>
          </div>
          {refs.length === 0 ? <p className="text-sm text-muted-foreground py-4">No referrals assigned yet.</p> : (
            <div className="divide-y divide-[#F0F4F7]">
              {refs.map((r) => (
                <div key={r.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0"><div className="text-sm font-medium text-[#252221] truncate">{laneLabel(r.referral_type)}</div><div className="text-xs text-muted-foreground">{r.family_name || "Referral"} · {formatDate(r.sent_date)}</div></div>
                  <StatusBadge value={r.status} />
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-[#252221]">Open tasks</h3>
            <Button variant="outline" size="sm" onClick={() => navigate("/portal/tasks")} data-testid="partner-view-tasks">View all <ArrowRight size={14} className="ml-1.5" /></Button>
          </div>
          {tasks.length === 0 ? <p className="text-sm text-muted-foreground py-4">No open tasks. Nicely done!</p> : (
            <div className="divide-y divide-[#F0F4F7]">
              {tasks.map((t) => (
                <div key={t.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0"><div className="text-sm font-medium text-[#252221] truncate">{t.title}</div><div className="text-xs text-muted-foreground">{t.due_date ? `Due ${formatDate(t.due_date)}` : "No due date"}</div></div>
                  <StatusBadge value={t.status} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.10)" }}><Building2 size={20} className="text-[#1C4358]" /></div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-[#252221]">{profile.name || "Your company"}</div>
            <div className="text-xs text-muted-foreground">{[profile.category && prettify(profile.category), profile.city, profile.state].filter(Boolean).join(" · ") || "Partner"}</div>
          </div>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => navigate("/portal/profile")} data-testid="partner-view-profile">Profile</Button>
        </div>
      </Card>
    </div>
  );
}
